import React, { useCallback, useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from 'tailwind-components/Modal/Modal';
import Button from 'tailwind-components/Button/Button';
import { stateAbbreviations } from 'constants/enum';
import { DEFAULT_EMPTY_HIGHLIGHT_MESSAGE } from './featuredCarrierMessages';
import { cn } from 'lib/utils';
import {
  agentRecommendationContact,
  formatRecommendationStatesForEmail,
  saveCarrierMissingAppointmentsRecommendation,
  sendAgencyRecommendationEmail
} from './agencyRecommendationTracking';

const AGENTS_PER_PAGE = 1;

/**
 * Modal launched from the FeaturedCarrierCard's "View details" CTA. Lists one
 * card per agent that hasn't been appointed with the featured carrier yet,
 * showing the states they're missing and a per-agent CTA.
 */

function toStateAbbr(state) {
  const value = String(state || '').trim();
  if (!value) return '';
  if (value.length === 2) return value.toUpperCase();
  return stateAbbreviations[value] || value;
}

function AgentBreakdownCard({
  agent,
  carrierName,
  carrierId,
  agencyId,
  agencyName,
  mainZohoId,
  onSuggested
}) {
  const [sendingEmail, setSendingEmail] = useState(false);
  const displayAgentName = agent?.agentName || 'Unknown agent';
  const stateAbbrs = Array.isArray(agent?.missingStates)
    ? agent.missingStates.map(toStateAbbr).filter(Boolean)
    : [];

  const handleSuggest = async () => {
    if (sendingEmail) return;

    const agentId = agent?.agentId;
    const { agentName, agentEmail, agentNpn } = agentRecommendationContact(agent);
    const stateForZoho = formatRecommendationStatesForEmail(agent?.missingStates);

    setSendingEmail(true);
    try {
      const sent = await sendAgencyRecommendationEmail({
        agentName,
        mainZohoId,
        agencyName: agent?.agencyName || agencyName,
        carrierName,
        state: stateForZoho,
        npn: agentNpn,
        agentEmail
      });

      if (!sent) return;

      const resolvedAgencyId = agent?.agencyId || agencyId;
      if (resolvedAgencyId && carrierId && agentId) {
        const entries =
          stateAbbrs.length > 0
            ? stateAbbrs.map(state => ({ agentId, state }))
            : [{ agentId, state: '' }];
        void saveCarrierMissingAppointmentsRecommendation({
          agencyId: resolvedAgencyId,
          carrierId,
          entries
        });
      }

      onSuggested?.();
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <article
      className={cn(
        'flex w-full min-w-0 flex-col gap-3 overflow-hidden rounded-[10px]',
        'border border-grey-lighter bg-white p-4 shadow-[0_2px_6px_rgba(0,0,0,0.06)]'
      )}
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 flex-col gap-0.5'>
          <h4 className='m-0 text-base leading-snug font-semibold text-primary-darkened'>
            {displayAgentName}
          </h4>
          {agent?.agencyName && (
            <p className='m-0 text-xs text-grey'>{agent.agencyName}</p>
          )}
          {stateAbbrs.length > 0 ? (
            <p className='m-0 text-xs text-grey'>
              Not appointed in {stateAbbrs.length}{' '}
              {stateAbbrs.length === 1 ? 'state' : 'states'}
            </p>
          ) : (
            <p className='m-0 text-xs text-grey'>
              Not currently appointed with {carrierName}
            </p>
          )}
        </div>
        <Button
          variant='primary'
          size='xsmall'
          rounded='small'
          text='Suggest to agent'
          onClick={handleSuggest}
          isLoading={sendingEmail}
          isDisabled={sendingEmail}
          className='shrink-0'
        />
      </div>
      {stateAbbrs.length > 0 && (
        <div className='flex flex-wrap gap-1.5'>
          {stateAbbrs.map(abbr => (
            <span
              key={abbr}
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5',
                'bg-primary-light text-primary-darkened',
                'text-[11px] font-bold tracking-[0.04em]'
              )}
            >
              {abbr}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

AgentBreakdownCard.propTypes = {
  agent: propTypes.shape({
    agentId: propTypes.string,
    agentName: propTypes.string,
    agencyName: propTypes.string,
    agencyId: propTypes.string,
    missingStates: propTypes.arrayOf(propTypes.string)
  }).isRequired,
  carrierName: propTypes.string.isRequired,
  carrierId: propTypes.string,
  agencyId: propTypes.string,
  agencyName: propTypes.string,
  mainZohoId: propTypes.string,
  onSuggested: propTypes.func
};

function FeaturedCarrierAgentsModal({
  open,
  onClose,
  carrierName,
  carrierId,
  agencyId,
  agencyName,
  mainZohoId,
  agents,
  emptyMessage
}) {
  const safeAgents = useMemo(
    () => (Array.isArray(agents) ? agents : []),
    [agents]
  );
  const [dismissedAgentIds, setDismissedAgentIds] = useState(() => new Set());
  const [page, setPage] = useState(0);

  useEffect(() => {
    setDismissedAgentIds(new Set());
    setPage(0);
  }, [open, safeAgents]);

  const visibleAgents = useMemo(
    () =>
      safeAgents.filter(
        a => a?.agentId && !dismissedAgentIds.has(String(a.agentId))
      ),
    [safeAgents, dismissedAgentIds]
  );
  const count = visibleAgents.length;
  const pageCount = Math.max(1, Math.ceil(count / AGENTS_PER_PAGE));

  useEffect(() => {
    setPage(p => Math.min(p, Math.max(0, pageCount - 1)));
  }, [pageCount, count]);

  const handleAgentSuggested = useCallback(agentId => {
    if (!agentId) return;
    setDismissedAgentIds(prev => new Set([...prev, String(agentId)]));
  }, []);

  if (!open) return null;

  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * AGENTS_PER_PAGE;
  const end = start + AGENTS_PER_PAGE;
  const pageAgents = visibleAgents.slice(start, end);

  const emptyStateMessage =
    (emptyMessage || '').trim() || DEFAULT_EMPTY_HIGHLIGHT_MESSAGE;

  const titleNode = (
    <div className='flex flex-col gap-0.5'>
      <span className='text-base font-semibold text-primary-darkened'>
        {carrierName
          ? `Agents missing appointments with ${carrierName}`
          : 'Agents missing appointments'}
      </span>
      <span className='text-xs font-normal text-grey'>
        {count === 0
          ? emptyStateMessage
          : `${count} ${count === 1 ? 'agent' : 'agents'} not yet appointed.`}
      </span>
    </div>
  );

  const footerNode = count > 1 && (
    <div className='flex w-full items-center justify-between gap-3'>
      <Button
        variant='default'
        outlined
        size='xsmall'
        rounded='small'
        icon={<ChevronLeft />}
        text='Previous'
        onClick={() => setPage(p => (p - 1 + pageCount) % pageCount)}
      />
      <span className='text-xs font-medium text-grey-dark tabular-nums'>
        {safePage + 1} of {count}
      </span>
      <Button
        variant='default'
        outlined
        size='xsmall'
        rounded='small'
        text='Next'
        icon={<ChevronRight />}
        iconPosition='right'
        onClick={() => setPage(p => (p + 1) % pageCount)}
      />
    </div>
  );

  return (
    <Modal
      active={open}
      closeModal={onClose}
      size='large'
      title={titleNode}
      footer={footerNode}
      hideFooter={!footerNode}
    >
      {count === 0 ? (
        <div className='flex min-h-[160px] items-center justify-center text-sm text-grey'>
          {emptyStateMessage}
        </div>
      ) : (
        <div className='flex flex-col gap-3'>
          {pageAgents.map((agent, idx) => (
            <AgentBreakdownCard
              key={String(
                agent?.agentId ?? agent?.agentName ?? `agent-${start + idx}`
              )}
              agent={agent}
              carrierName={carrierName}
              carrierId={carrierId}
              agencyId={agencyId}
              agencyName={agencyName}
              mainZohoId={mainZohoId}
              onSuggested={() => handleAgentSuggested(agent?.agentId)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

FeaturedCarrierAgentsModal.propTypes = {
  open: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
  carrierName: propTypes.string,
  carrierId: propTypes.string,
  agencyId: propTypes.string,
  agencyName: propTypes.string,
  mainZohoId: propTypes.string,
  agents: propTypes.arrayOf(
    propTypes.shape({
      agentId: propTypes.string,
      agentName: propTypes.string,
      missingStates: propTypes.arrayOf(propTypes.string)
    })
  ),
  emptyMessage: propTypes.string
};

export default FeaturedCarrierAgentsModal;
